/**
 * Cognito Error Mapper
 * 
 * Maps Cognito error codes to user-friendly messages.
 * Provides centralized error handling for authentication operations.
 */

/**
 * Cognito error interface
 */
export interface CognitoError {
  code?: string;
  name?: string;
  message?: string;
}

/**
 * User-friendly error messages for Cognito error codes
 */
const ERROR_MESSAGES: Record<string, string> = {
  // Authentication errors
  UserNotFoundException: 'Invalid email or password',
  NotAuthorizedException: 'Invalid email or password',
  UserNotConfirmedException: 'Please verify your email address before signing in',
  PasswordResetRequiredException: 'Password reset is required. Please reset your password',
  
  // Session errors (different from auth errors)
  SessionExpired: 'Session expired. Please sign in again',
  
  // Sign up errors
  UsernameExistsException: 'An account with this email already exists',
  InvalidPasswordException: 'Password does not meet requirements',
  InvalidParameterException: 'Invalid input provided',
  
  // Verification errors
  CodeMismatchException: 'Invalid verification code',
  ExpiredCodeException: 'Verification code has expired. Please request a new code',
  AliasExistsException: 'An account with this email already exists',
  
  // Rate limiting errors
  LimitExceededException: 'Too many attempts. Please try again later',
  TooManyRequestsException: 'Too many requests. Please try again in a few minutes',
  TooManyFailedAttemptsException: 'Too many failed attempts. Your account has been temporarily locked',
  
  // Network/Service errors
  NetworkError: 'Network error. Please check your internet connection',
  TimeoutError: 'Request timed out. Please try again',
  ServiceUnavailable: 'Service temporarily unavailable. Please try again later',
  
  // Validation errors
  ValidationError: 'Validation error',
};

/**
 * Map Cognito error to user-friendly message
 * 
 * @param error - Error object from Cognito or other sources
 * @returns User-friendly error message
 */
export const mapCognitoError = (error: any): string => {
  // Handle null/undefined errors
  if (!error) {
    return 'An unknown error occurred';
  }

  // Handle ValidationError from our own validation
  if (error.name === 'ValidationError') {
    return error.message || 'Validation error';
  }

  // Get error code from various possible locations
  const errorCode = error?.code || error?.name;
  
  // Return mapped message or original message or default
  if (errorCode && ERROR_MESSAGES[errorCode]) {
    return ERROR_MESSAGES[errorCode];
  }

  // Return original message if available
  if (error.message) {
    return error.message;
  }

  // Default fallback
  return 'An unexpected error occurred. Please try again';
};

/**
 * Check if error is a network error
 * 
 * @param error - Error object
 * @returns True if error is network-related
 */
export const isNetworkError = (error: any): boolean => {
  return (
    error?.code === 'NetworkError' ||
    error?.name === 'NetworkError' ||
    error?.message?.includes('Network') ||
    error?.message?.includes('network') ||
    !error?.response
  );
};

/**
 * Check if error is a timeout error
 * 
 * @param error - Error object
 * @returns True if error is timeout-related
 */
export const isTimeoutError = (error: any): boolean => {
  return !!(
    error?.code === 'TimeoutError' ||
    error?.name === 'TimeoutError' ||
    error?.code === 'ECONNABORTED' ||
    error?.message?.includes('timeout') ||
    error?.message?.includes('Timeout')
  );
};

/**
 * Check if error is retryable
 * 
 * @param error - Error object
 * @returns True if error should be retried
 */
export const isRetryableError = (error: any): boolean => {
  const errorCode = error?.code || error?.name;
  
  // Rate limiting errors are not immediately retryable - check first
  if (
    errorCode === 'TooManyRequestsException' ||
    errorCode === 'LimitExceededException' ||
    errorCode === 'TooManyFailedAttemptsException'
  ) {
    return false;
  }
  
  // Authentication errors are not retryable
  if (
    errorCode === 'UserNotFoundException' ||
    errorCode === 'NotAuthorizedException' ||
    errorCode === 'UserNotConfirmedException' ||
    errorCode === 'InvalidPasswordException' ||
    errorCode === 'CodeMismatchException' ||
    errorCode === 'ExpiredCodeException'
  ) {
    return false;
  }
  
  // Network and timeout errors are retryable
  if (isNetworkError(error) || isTimeoutError(error)) {
    return true;
  }
  
  // Service unavailable errors are retryable
  if (errorCode === 'ServiceUnavailable' || errorCode === 'InternalErrorException') {
    return true;
  }
  
  return false;
};

/**
 * Get retry delay in milliseconds based on attempt number
 * Uses exponential backoff: 1s, 2s, 4s
 * 
 * @param attempt - Current attempt number (0-indexed)
 * @returns Delay in milliseconds
 */
export const getRetryDelay = (attempt: number): number => {
  const baseDelay = 1000; // 1 second
  const maxDelay = 4000; // 4 seconds
  const delay = baseDelay * Math.pow(2, attempt);
  return Math.min(delay, maxDelay);
};

/**
 * Sleep for specified milliseconds
 * 
 * @param ms - Milliseconds to sleep
 * @returns Promise that resolves after delay
 */
export const sleep = (ms: number): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms));
};
